/**
 * Dateityp einer Datei — fachliche Art, Kürzel, Name und Farbe. Eine Quelle für
 * alle farbigen Datei-Symbole (Explorer, Vorgang, Kommentar-Anhänge).
 *
 * Die Einordnung stand bis Oktober 2026 nur im Dokumente-Explorer
 * (`explorer/fileTypeIcons.tsx`); der Vorgang zeigte jedes Dokument mit
 * demselben grauen Blatt (User 22.10.2026: „im Vorgang muss man direkt
 * erkennen, ob PDF, Excel, Bild, …"). Damit Explorer und Vorgang dieselbe
 * Datei nicht verschieden einordnen, liegt die Regel jetzt hier und der
 * Explorer importiert sie.
 *
 * Bewusst DOM- und React-frei, damit der (node-only) Vitest-Runner sie prüfen
 * kann.
 */

import { extOf } from "../../utils/fileUtils";
import { isPdfLike } from "./documentKind";

export type FileKindKey =
    | "pdf"
    | "word"
    | "excel"
    | "powerpoint"
    | "image"
    | "archive"
    | "mail"
    | "video"
    | "audio"
    | "text"
    | "other";

/** Farben der Dateitypen — an Office/Windows angelehnt, in KMVS-Tönen. */
export const FILE_TYPE_COLORS = {
    folder: "#f0a020", // = --gold (CI-Akzent, zugleich das klassische Ordner-Gelb)
    pdf: "#c8322b",
    word: "#2b579a",
    excel: "#1d7044",
    powerpoint: "#d24726",
    image: "#b21f93", // = --mag
    archive: "#8a6d1f",
    mail: "#3a6ea5", // = --info
    media: "#6e1c5c", // = --aub-600
    text: "#6b5a66", // = --ink-2
    other: "#9a8c95", // = --ink-3
} as const;

const WORD = new Set(["doc", "docx", "docm", "dot", "dotx", "rtf", "odt"]);
const SHEET = new Set(["xls", "xlsx", "xlsm", "xlsb", "csv", "ods"]);
const SLIDE = new Set(["ppt", "pptx", "ppsx", "odp"]);
const IMAGE = new Set(["jpg", "jpeg", "png", "gif", "bmp", "webp", "tif", "tiff", "heic", "svg"]);
const ARCHIVE = new Set(["zip", "rar", "7z", "tar", "gz", "bz2"]);
const MAIL = new Set(["msg", "eml", "oft"]);
const VIDEO = new Set(["mp4", "mov", "avi", "mkv", "wmv", "webm"]);
const AUDIO = new Set(["mp3", "wav", "m4a", "aac", "ogg", "wma"]);
const TEXT = new Set(["txt", "log", "md", "xml", "json", "htm", "html", "csv"]);

/**
 * Fachlicher Dateityp aus Dateiname + MIME. Die PDF-Erkennung delegiert an
 * `isPdfLike()` aus documentKind — eine Regel, eine Quelle (sonst
 * driften Vorschau-Fähigkeit und Icon auseinander).
 */
export const fileKind = (fileName: string | null | undefined, mimeType?: string | null): FileKindKey => {
    const name = fileName ?? "";
    if (isPdfLike(name, mimeType)) return "pdf";
    const m = (mimeType || "").toLowerCase();
    const e = extOf(name);
    if (m.startsWith("image/") || IMAGE.has(e)) return "image";
    if (m.includes("sheet") || m.includes("excel") || SHEET.has(e)) return "excel";
    if (m.includes("presentation") || m.includes("powerpoint") || SLIDE.has(e)) return "powerpoint";
    if (m.includes("word") || m.includes("opendocument.text") || WORD.has(e)) return "word";
    if (m.includes("zip") || m.includes("compressed") || ARCHIVE.has(e)) return "archive";
    if (m.includes("message") || m.includes("outlook") || MAIL.has(e)) return "mail";
    if (m.startsWith("video/") || VIDEO.has(e)) return "video";
    if (m.startsWith("audio/") || AUDIO.has(e)) return "audio";
    if (m.startsWith("text/") || TEXT.has(e)) return "text";
    return "other";
};

/** Anzeigename des Typs (Windows: „PDF-Dokument"). */
export const KIND_LABEL: Record<FileKindKey, string> = {
    pdf: "PDF-Dokument",
    word: "Word-Dokument",
    excel: "Excel-Tabelle",
    powerpoint: "Präsentation",
    image: "Bild",
    archive: "Archiv",
    mail: "E-Mail",
    video: "Video",
    audio: "Audio",
    text: "Textdatei",
    other: "Datei",
};

/** Farbe je Art; Video und Audio teilen sich den Medien-Ton. */
export const KIND_COLOR: Record<FileKindKey, string> = {
    pdf: FILE_TYPE_COLORS.pdf,
    word: FILE_TYPE_COLORS.word,
    excel: FILE_TYPE_COLORS.excel,
    powerpoint: FILE_TYPE_COLORS.powerpoint,
    image: FILE_TYPE_COLORS.image,
    archive: FILE_TYPE_COLORS.archive,
    mail: FILE_TYPE_COLORS.mail,
    video: FILE_TYPE_COLORS.media,
    audio: FILE_TYPE_COLORS.media,
    text: FILE_TYPE_COLORS.text,
    other: FILE_TYPE_COLORS.other,
};

/**
 * Kürzel je Art, wenn die Endung nichts Brauchbares hergibt. Bei Office
 * absichtlich die alten Dreibuchstaber („DOC", nicht „DOCX") — das Symbol soll
 * die Familie zeigen, nicht die Version.
 */
const KIND_KUERZEL: Record<FileKindKey, string> = {
    pdf: "PDF",
    word: "DOC",
    excel: "XLS",
    powerpoint: "PPT",
    image: "BILD",
    archive: "ZIP",
    mail: "MAIL",
    video: "VID",
    audio: "AUD",
    text: "TXT",
    other: "",
};

/**
 * Kürzel im Symbol — damit die Farbe nicht das einzige Merkmal ist.
 *
 * Office, PDF und ZIP-Varianten zeigen die Familie („XLS" auch für .xlsm),
 * Bilder, Mails und Text ihre Endung („PNG", „MSG", „CSV"), weil die dort
 * etwas sagt. Höchstens vier Zeichen; ohne verwertbare Endung bleibt bei
 * „sonstige" ein leeres Blatt.
 */
export const dateiTypKuerzel = (fileName: string | null | undefined, mimeType?: string | null): string => {
    const art = fileKind(fileName, mimeType);
    const e = extOf(fileName ?? "");
    const endung = /^[a-z0-9]{1,4}$/.test(e) ? e.toUpperCase() : "";
    switch (art) {
        case "image":
            return e === "jpeg" ? "JPG" : e === "tiff" ? "TIF" : endung || KIND_KUERZEL.image;
        case "mail":
        case "text":
            return endung || KIND_KUERZEL[art];
        case "excel":
            return e === "csv" ? "CSV" : KIND_KUERZEL.excel;
        case "other":
            return endung;
        default:
            return KIND_KUERZEL[art];
    }
};

export type DateiTypInfo = {
    art: FileKindKey;
    /** Kurzzeichen im Symbol, z. B. „PDF", „XLS", „PNG"; leer bei unbekannt. */
    kuerzel: string;
    /** Ausgeschriebener Typ für Tooltip und Screenreader, z. B. „Excel-Tabelle". */
    label: string;
    farbe: string;
};

/** Alles, was ein Datei-Symbol braucht, in einem Zug. */
export const dateiTypInfo = (fileName: string | null | undefined, mimeType?: string | null): DateiTypInfo => {
    const art = fileKind(fileName, mimeType);
    return {
        art,
        kuerzel: dateiTypKuerzel(fileName, mimeType),
        label: KIND_LABEL[art],
        farbe: KIND_COLOR[art],
    };
};
